import type {
  FramingArtifact,
  FramingCoordinateSpace,
  FramingDeliveredSubjectEvidence,
  FramingEvidenceMode,
} from "./framingInspector";

const CONTACT_SHEET_KINDS = new Set([
  "framing_actual_contact_sheet",
  "framing_hypothetical_preview",
]);

type FramingArtifactCorrelation = {
  attemptId: string | null;
  taskId: string | null;
  planDigest: string | null;
};

export function isFramingContactSheetArtifact(artifact: FramingArtifact) {
  return CONTACT_SHEET_KINDS.has(artifact.kind ?? "");
}

export function selectCurrentFramingContactSheets(
  artifacts: FramingArtifact[],
  correlation: FramingArtifactCorrelation,
): FramingArtifact[] {
  if (!correlation.attemptId || !correlation.planDigest) return [];
  return artifacts.filter(
    (artifact) =>
      isFramingContactSheetArtifact(artifact) &&
      Boolean(artifact.url) &&
      artifact.attemptId === correlation.attemptId &&
      artifact.planDigest === correlation.planDigest &&
      (correlation.taskId
        ? artifact.taskId === correlation.taskId
        : artifact.taskId == null),
  );
}

export function deliveredEvidenceAtTime(
  evidence: FramingDeliveredSubjectEvidence[],
  coordinateSpaceId: string | null,
  timeSeconds: number,
): FramingDeliveredSubjectEvidence[] {
  const inSpace = evidence.filter(
    (item) => item.coordinateSpaceId === coordinateSpaceId,
  );
  return inSpace.filter((item, index) => {
    const subjectId = item.subjectId ?? `unidentified-${index}`;
    const distance = Math.abs(
      (item.sceneTimestampSeconds ?? timeSeconds) - timeSeconds,
    );
    return !inSpace.some(
      (candidate, candidateIndex) =>
        candidateIndex !== index &&
        (candidate.subjectId ?? `unidentified-${candidateIndex}`) ===
          subjectId &&
        Math.abs(
          (candidate.sceneTimestampSeconds ?? timeSeconds) - timeSeconds,
        ) < distance,
    );
  });
}

export function framingOverlayEvidencePolicy(
  evidenceMode: FramingEvidenceMode,
  evidenceRole?: FramingCoordinateSpace["evidenceRole"],
): { plan: boolean; applied: boolean } {
  return {
    plan:
      evidenceMode === "actual" ||
      (evidenceMode === "proposed" && evidenceRole === "proposed_enforce"),
    applied: evidenceMode === "actual" && evidenceRole !== "proposed_enforce",
  };
}

export function clampSceneRelativeTime(
  timeSeconds: number,
  timelineStartSeconds: number,
  timelineEndSeconds?: number | null,
  fps = 30,
): { relativeSeconds: number; absoluteSeconds: number; atEnd: boolean } {
  const duration =
    timelineEndSeconds != null
      ? Math.max(0, timelineEndSeconds - timelineStartSeconds)
      : null;
  const relativeSeconds = Math.max(
    0,
    duration == null ? timeSeconds : Math.min(timeSeconds, duration),
  );
  const atEnd = duration != null && relativeSeconds >= duration;
  // timelineEndSeconds is exclusive: it is the first timestamp owned by the
  // next scene. Keep the logical slider at the full duration while seeking the
  // media element to the final presentation timestamp inside this scene.
  const mediaRelativeSeconds =
    atEnd && duration != null
      ? Math.max(0, duration - 1 / Math.max(1, fps))
      : relativeSeconds;
  return {
    relativeSeconds,
    absoluteSeconds: timelineStartSeconds + mediaRelativeSeconds,
    atEnd,
  };
}
